import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ROUTES } from "../../constants/routes";
import Header from "../../components/header/Header";
// @ts-ignore vendored plain-JS engine (gnovotny/nothing-to-watch)
import voroforce from "../../vendor/voroforce";
// @ts-ignore
import { isTouchDevice } from "../../vendor/voroforce/utils/device";
import config, { CELLS, MEDIA_BASE_URL, SUBGRID_SIZE } from "./vf/config";
import { localizedTitle, pickCellIdOffset } from "./vf/slice";

const JSON_BASE_URL = process.env.REACT_APP_VORO_JSON_URL || "/voro/json";
const DATA_BASE_URL = JSON_BASE_URL.replace(/\/json$/, "");
const POSTER_BASE_URL = "https://image.tmdb.org/t/p/w185";

type Movie = {
  id: number;
  title: string;
  title_ru?: string;
  title_ua?: string;
  title_ar?: string;
  poster_path?: string;
  release_date?: string;
  vote_average?: number;
};

type Cell = { id: number; subgrid: number; subgridIndex: number };

const batches = new Map<number, Movie[]>();

const getCellMovie = async (cell: Cell): Promise<Movie | undefined> => {
  let batch = batches.get(cell.subgrid);
  if (!batch) {
    try {
      const res = await fetch(`${JSON_BASE_URL}/${cell.subgrid}.json`);
      batch = res.ok ? await res.json() : [];
    } catch {
      batch = [];
    }
    batches.set(cell.subgrid, batch as Movie[]);
  }
  return batch?.[cell.subgridIndex % SUBGRID_SIZE];
};

const ExplorePage = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [movie, setMovie] = useState<Movie>();
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let instance: any;
    let disposed = false;
    // ponytail: flat halving on touch. Upstream picks cell counts per GPU class with
    // detect-gpu; swap that in if phones still struggle.
    const cells = isTouchDevice ? Math.round(CELLS / 2) : CELLS;

    const start = async () => {
      // The baked pool is bigger than the number of cells on screen, so start the cell ids
      // at a random batch boundary: every visit shows a different slice of the catalogue.
      let cellIdOffset = 0;
      try {
        const res = await fetch(`${DATA_BASE_URL}/manifest.json`);
        if (!res.ok) throw new Error(`${res.status}`);
        const { count, subgridSize = SUBGRID_SIZE } = await res.json();
        cellIdOffset = pickCellIdOffset(count, cells, subgridSize);
      } catch (e) {
        console.error("voro manifest unavailable", e); // no data at all -> the probe below reports it
        setFailed(true);
        return;
      }
      if (disposed) return;

      // Needs WebGL2 as well as the atlases; either can be missing in a deployed
      // environment, so fail visibly instead of showing black.
      try {
        instance = voroforce(container, {
          ...config,
          cells,
          lattice: { ...config.lattice, cellIdOffset },
        });
      } catch (e) {
        console.error("voroforce init failed", e);
        setFailed(true);
        return;
      }

      const openMovie = (selected: Movie) => navigate(`${ROUTES.DETAILS}/${selected.id}`);
      let tappedId: number | undefined;

      instance.controls.listen("focused", ({ cell }: { cell?: Cell }) => {
        if (cell) void getCellMovie(cell).then(setMovie);
      });
      instance.controls.listen("selected", ({ cell }: { cell?: Cell }) => {
        if (!cell) return;
        void getCellMovie(cell).then((selected) => {
          if (!selected) return;
          setMovie(selected);
          // On touch there is no hover: the first tap only brings a cell up, a second tap
          // on the same cell opens it. Keeps a stray tap from yanking you off the page.
          if (isTouchDevice && tappedId !== cell.id) {
            tappedId = cell.id;
            return;
          }
          openMovie(selected);
        });
      });
    };

    void start();

    void fetch(`${MEDIA_BASE_URL}/low/dds/0.dds`, { method: "HEAD" })
      .then((res) => setFailed(!res.ok))
      .catch(() => setFailed(true));

    return () => {
      disposed = true;
      // ponytail: engine dispose() touches helpers that lack a dispose method upstream;
      // swallow so a failed teardown can't blank the app on route change.
      try {
        instance?.dispose();
      } catch (e) {
        console.warn("voroforce dispose failed", e);
      }
      container.replaceChildren();
    };
  }, [navigate]);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "#000",
        // the engine sets touch-action on its container; this stops the page itself from
        // pull-to-refreshing or swiping back underneath the gallery
        touchAction: "none",
        overscrollBehavior: "none",
      }}
    >
      <div ref={containerRef} style={{ width: "100%", height: "100%" }} />
      <Header />
      {failed && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            opacity: 0.6,
            textAlign: "center",
            padding: 24,
          }}
        >
          Gallery unavailable — needs WebGL2 and the generated poster atlases.
        </div>
      )}
      {movie && (
        <div
          style={{
            position: "absolute",
            bottom: 24,
            left: 24,
            display: "flex",
            gap: 12,
            alignItems: "flex-end",
            color: "#fff",
            pointerEvents: "none",
            textShadow: "0 1px 4px rgba(0,0,0,.8)",
          }}
        >
          {movie.poster_path && (
            <img src={`${POSTER_BASE_URL}${movie.poster_path}`} alt="" width={60} style={{ borderRadius: 4 }} />
          )}
          <div>
            <div style={{ fontSize: 20, fontWeight: 600 }}>{localizedTitle(movie, i18n.language)}</div>
            <div style={{ opacity: 0.7, fontSize: 14 }}>
              {movie.release_date?.slice(0, 4)}
              {movie.vote_average ? ` · ${movie.vote_average.toFixed(1)}` : ""}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExplorePage;
